import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { shipmentsAPI, warehouseAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Truck, X, Upload, FileText, Eye, XCircle } from 'lucide-react';

export const Shipments = () => {
  const [shipments, setShipments] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const [newShipment, setNewShipment] = useState({
    name: '',
    notes: '',
    cartonProducts: [
      { product_id: '', name: '', quantity: 0, weight_per_unit: 0 },
    ],
  });

  const fetchShipments = async () => {
    setLoading(true);
    try {
      const [shipmentsRes, stockRes] = await Promise.all([
        shipmentsAPI.getAll(),
        warehouseAPI.getStock(),
      ]);
      setShipments(shipmentsRes.data);
      setStock(stockRes.data);
    } catch (error) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  const addCartonProduct = () => {
    setNewShipment({
      ...newShipment,
      cartonProducts: [
        ...newShipment.cartonProducts,
        { product_id: '', name: '', quantity: 0, weight_per_unit: 0 },
      ],
    });
  };

  const updateCartonProduct = (index, field, value) => {
    const products = [...newShipment.cartonProducts];
    products[index][field] = value;
    
    if (field === 'product_id') {
      const stockItem = stock.find(s => s.product_id === value);
      if (stockItem) {
        products[index].name = stockItem.name;
      }
    }
    
    setNewShipment({ ...newShipment, cartonProducts: products });
  };

  const removeCartonProduct = (index) => {
    if (newShipment.cartonProducts.length > 1) {
      const products = newShipment.cartonProducts.filter((_, i) => i !== index);
      setNewShipment({ ...newShipment, cartonProducts: products });
    }
  };

  const calculateCartonWeight = () => {
    return newShipment.cartonProducts.reduce(
      (sum, p) => sum + (p.quantity * p.weight_per_unit / 1000),
      0
    );
  };

  const handleCreateShipment = async () => {
    const cartonWeight = calculateCartonWeight();
    
    if (cartonWeight < 8 || cartonWeight > 9) {
      toast.error(`Carton weight must be between 8-9 kg. Current: ${cartonWeight.toFixed(2)} kg`);
      return;
    }

    try {
      // Create 5 identical cartons
      const cartons = Array(5).fill(null).map((_, i) => ({
        carton_number: i + 1,
        products: newShipment.cartonProducts.map(p => ({
          product_id: p.product_id,
          name: p.name,
          quantity: parseInt(p.quantity),
        })),
        weight_kg: cartonWeight,
      }));

      await shipmentsAPI.create({
        name: newShipment.name,
        cartons,
        notes: newShipment.notes,
      });

      toast.success('Shipment created successfully');
      setCreateDialogOpen(false);
      setNewShipment({
        name: '',
        notes: '',
        cartonProducts: [{ product_id: '', name: '', quantity: 0, weight_per_unit: 0 }],
      });
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shipment');
    }
  };

  const handleCancelShipment = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this shipment? Stock will be reverted.')) return;
    try {
      await shipmentsAPI.cancel(id);
      toast.success('Shipment cancelled and stock reverted');
      fetchShipments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel shipment');
    }
  };

  const handleUploadPdf = async (e, shipmentId) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPdf(true);
    try {
      await shipmentsAPI.uploadPdf(shipmentId, file);
      toast.success('PDF uploaded successfully');
      fetchShipments();
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const viewShipmentDetails = (shipment) => {
    setSelectedShipment(shipment);
    setDetailDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      created: 'badge-info',
      shipped: 'badge-warning',
      delivered: 'badge-success',
      cancelled: 'badge-danger',
    };
    return `badge ${badges[status] || 'badge-neutral'}`;
  };

  return (
    <Layout>
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title" data-testid="shipments-title">Shipments</h1>
          <p className="page-subtitle">Create and manage FBA shipments with 5 identical cartons</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-shipment-btn">
              <Plus size={16} className="mr-2" />
              Create Shipment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Shipment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Shipment Name</Label>
                <Input
                  value={newShipment.name}
                  onChange={(e) => setNewShipment({ ...newShipment, name: e.target.value })}
                  placeholder="FBA Shipment January 2025"
                  data-testid="shipment-name-input"
                />
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  Configure products for ONE carton. 5 identical cartons will be created.
                </p>
                <p className="text-xs text-blue-600">
                  Each carton must weigh between 8-9 kg.
                </p>
              </div>

              <div>
                <Label>Products per Carton</Label>
                <div className="space-y-2 mt-2">
                  {newShipment.cartonProducts.map((product, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-end">
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={product.product_id}
                        onChange={(e) => updateCartonProduct(index, 'product_id', e.target.value)}
                        data-testid={`carton-product-${index}`}
                      >
                        <option value="">Select product</option>
                        {stock.map((s) => (
                          <option key={s.product_id} value={s.product_id}>
                            {s.name} (Stock: {s.quantity})
                          </option>
                        ))}
                      </select>
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={product.quantity}
                        onChange={(e) => updateCartonProduct(index, 'quantity', e.target.value)}
                        data-testid={`carton-qty-${index}`}
                      />
                      <Input
                        type="number"
                        placeholder="Weight (g)"
                        value={product.weight_per_unit}
                        onChange={(e) => updateCartonProduct(index, 'weight_per_unit', e.target.value)}
                        data-testid={`carton-weight-${index}`}
                      />
                      <div className="text-sm text-slate-500">
                        {((product.quantity * product.weight_per_unit) / 1000).toFixed(2)} kg
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeCartonProduct(index)}
                        disabled={newShipment.cartonProducts.length === 1}
                      >
                        <X size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="mt-2" onClick={addCartonProduct}>
                  <Plus size={14} className="mr-1" /> Add Product
                </Button>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Carton Weight:</span>
                  <span className={`text-lg font-bold ${calculateCartonWeight() >= 8 && calculateCartonWeight() <= 9 ? 'text-green-600' : 'text-red-600'}`}>
                    {calculateCartonWeight().toFixed(2)} kg
                  </span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="font-medium">Total Shipment Weight (5 cartons):</span>
                  <span className="text-lg font-bold">
                    {(calculateCartonWeight() * 5).toFixed(2)} kg
                  </span>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Input
                  value={newShipment.notes}
                  onChange={(e) => setNewShipment({ ...newShipment, notes: e.target.value })}
                  placeholder="Optional notes"
                  data-testid="shipment-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleCreateShipment}
                disabled={calculateCartonWeight() < 8 || calculateCartonWeight() > 9}
                data-testid="submit-shipment-btn"
              >
                Create Shipment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <Truck size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Shipments</p>
              <p className="text-2xl font-bold">{shipments.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Active</p>
            <p className="text-2xl font-bold text-green-600">
              {shipments.filter(s => s.status === 'created').length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Cartons</p>
            <p className="text-2xl font-bold">
              {shipments.reduce((sum, s) => sum + (s.cartons?.length || 0), 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Weight</p>
            <p className="text-2xl font-bold">
              {shipments.reduce((sum, s) => sum + (s.total_weight_kg || 0), 0).toFixed(1)} kg
            </p>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="shipments-table-card">
        <CardHeader>
          <CardTitle className="text-lg">All Shipments</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shipment ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Cartons</TableHead>
                  <TableHead>Total Weight</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>PDF Files</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shipments.map((shipment) => (
                  <TableRow key={shipment.id} data-testid={`shipment-row-${shipment.id}`}>
                    <TableCell className="font-mono text-xs">{shipment.id.slice(0, 8)}...</TableCell>
                    <TableCell className="font-medium">{shipment.name}</TableCell>
                    <TableCell>{shipment.cartons?.length || 0}</TableCell>
                    <TableCell>{shipment.total_weight_kg?.toFixed(1)} kg</TableCell>
                    <TableCell>
                      <span className={getStatusBadge(shipment.status)}>{shipment.status}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <FileText size={14} className="text-slate-400" />
                        <span>{shipment.pdf_files?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {new Date(shipment.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewShipmentDetails(shipment)}
                          data-testid={`view-shipment-${shipment.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={(e) => handleUploadPdf(e, shipment.id)}
                            disabled={uploadingPdf}
                          />
                          <Button variant="ghost" size="icon" asChild>
                            <span><Upload size={16} /></span>
                          </Button>
                        </label>
                        {shipment.status !== 'cancelled' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCancelShipment(shipment.id)}
                            data-testid={`cancel-shipment-${shipment.id}`}
                          >
                            <XCircle size={16} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {shipments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      No shipments found. Create your first shipment.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Shipment Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck size={20} />
              {selectedShipment?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedShipment && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Status</p>
                  <span className={getStatusBadge(selectedShipment.status)}>
                    {selectedShipment.status}
                  </span>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Cartons</p>
                  <p className="text-xl font-bold">{selectedShipment.cartons?.length || 0}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Total Weight</p>
                  <p className="text-xl font-bold">{selectedShipment.total_weight_kg?.toFixed(1)} kg</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">PDF Files</p>
                  <p className="text-xl font-bold">{selectedShipment.pdf_files?.length || 0}</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-3">Carton Contents</h3>
                <div className="grid grid-cols-5 gap-4">
                  {selectedShipment.cartons?.map((carton, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-3">
                        <p className="font-medium text-sm mb-2">Carton #{carton.carton_number}</p>
                        <p className="text-xs text-slate-500 mb-2">{carton.weight_kg?.toFixed(2)} kg</p>
                        {carton.products?.map((p, pIdx) => (
                          <div key={pIdx} className="text-xs">
                            {p.name}: {p.quantity}
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {selectedShipment.notes && (
                <div>
                  <h3 className="font-semibold mb-2">Notes</h3>
                  <p className="text-slate-600">{selectedShipment.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
